import { useRef, useState } from 'react';
import { Ban, ImagePlus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { CategoryIcon } from './CategoryIcon';
import { BANK_ORDER, BRANDS, brandIconKey, brandSlugOf, isBrandIcon, isImageIcon } from './brands';

/** Reduz a imagem enviada a um PNG 96×96 (object-contain) → data URL leve p/ o campo icon. */
function fileToIconDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('leitura'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('imagem'));
      img.onload = () => {
        const S = 96;
        const canvas = document.createElement('canvas');
        canvas.width = S;
        canvas.height = S;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('canvas'));
        const scale = Math.min(S / img.width, S / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/** Ícones genéricos (não-marca) úteis para carteira/dinheiro/investimento. */
const GENERIC_ICONS = [
  'wallet',
  'banknote',
  'piggy-bank',
  'landmark',
  'credit-card',
  'circle-dollar-sign',
  'trending-up',
  'smartphone',
] as const;

interface BankPickerProps {
  /** Ícone atual: 'brand:<slug>', nome lucide, ou '' (nenhum). */
  value: string;
  /** Chama com o novo ícone; `brandColor` presente quando é um banco (sugere a cor). */
  onChange: (icon: string, brandColor?: string) => void;
  className?: string;
}

/** Seletor de banco (logos/marcas) + ícones genéricos, com aba "nenhum". */
export function BankPicker({ value, onChange, className }: BankPickerProps) {
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const banks = expanded ? BANK_ORDER : BANK_ORDER.slice(0, 12);
  const currentSlug = isBrandIcon(value) ? brandSlugOf(value) : null;
  const customImage = isImageIcon(value) ? value : null;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reenviar o mesmo arquivo
    if (!file) return;
    try {
      onChange(await fileToIconDataUrl(file));
    } catch {
      /* imagem inválida — ignora */
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Bancos */}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {banks.map((slug) => {
          const brand = BRANDS[slug];
          if (!brand) return null;
          const selected = currentSlug === slug;
          return (
            <button
              key={slug}
              type="button"
              aria-pressed={selected}
              aria-label={brand.label}
              title={brand.label}
              onClick={() => onChange(brandIconKey(slug), brand.hex)}
              className={cn(
                'flex flex-col items-center gap-1 rounded-lg border p-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                selected ? 'border-primary bg-primary-weak' : 'border-border hover:bg-canvas',
              )}
            >
              <CategoryIcon icon={brandIconKey(slug)} color={brand.hex} size={34} />
              <span className="w-full truncate text-center text-[10px] leading-tight text-muted">
                {brand.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[13px] font-semibold text-primary transition-colors hover:text-primary-hover"
        >
          {expanded ? 'Ver menos' : 'Ver mais bancos'}
        </button>
        <span className="h-px flex-1 bg-border" />
      </div>

      {/* Ícones genéricos + logo personalizado + nenhum */}
      <div className="flex flex-wrap items-center gap-2">
        {/* logo personalizado (imagem enviada pelo usuário) */}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        <button
          type="button"
          aria-pressed={!!customImage}
          title="Enviar logo (imagem)"
          onClick={() => fileRef.current?.click()}
          className={cn(
            'grid size-11 place-items-center overflow-hidden rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            customImage ? 'border-primary bg-primary-weak' : 'border-dashed border-field text-muted hover:bg-canvas',
          )}
        >
          {customImage ? (
            <CategoryIcon icon={customImage} color="#ffffff" size={30} />
          ) : (
            <ImagePlus className="size-5" />
          )}
        </button>

        {GENERIC_ICONS.map((name) => {
          const selected = value === name;
          return (
            <button
              key={name}
              type="button"
              aria-pressed={selected}
              aria-label={name}
              onClick={() => onChange(name)}
              className={cn(
                'grid size-11 place-items-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                selected ? 'border-primary bg-primary-weak' : 'border-border hover:bg-canvas',
              )}
            >
              <CategoryIcon icon={name} color="var(--color-muted)" size={30} />
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={value === ''}
          aria-label="Sem ícone"
          title="Sem ícone (usa a inicial)"
          onClick={() => onChange('')}
          className={cn(
            'grid size-11 place-items-center rounded-lg border text-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            value === '' ? 'border-primary bg-primary-weak text-primary' : 'border-border hover:bg-canvas',
          )}
        >
          <Ban className="size-5" />
        </button>
      </div>
    </div>
  );
}
